import type {PostHogConfig} from 'posthog-js';
import {ignoredSentryErrors} from '@common/errors/ignored-sentry-errors';

const REPLAY_SAMPLE_STORAGE_KEY = 'ph_pricing_checkout_replay_sample';

function stripQueryString(url: string): string {
  return url.split('?')[0] ?? url;
}

export function createPostHogOptions(
  host: string | undefined,
): Partial<PostHogConfig> {
  return {
    api_host: host || 'https://us.i.posthog.com',
    defaults: '2026-05-30',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    enable_recording_console_log: false,
    // Exception autocapture is enabled in the PostHog project settings.
    // Filter the same noisy browser errors Sentry ignores.
    before_send: event => {
      if (!event) {
        return event;
      }
      const message =
        (event.properties?.$exception_message as string | undefined) ||
        (event.properties?.message as string | undefined) ||
        '';
      if (
        typeof message === 'string' &&
        ignoredSentryErrors.some(ignored => message.includes(ignored))
      ) {
        return null;
      }
      return event;
    },
    tracing_headers:
      typeof window !== 'undefined' ? [window.location.hostname] : [],
    session_recording: {
      // Record typed values so checkout/pricing bugs are diagnosable.
      // Passwords stay masked; payment iframes are already excluded below. Never change this without explicit approval
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      recordCrossOriginIframes: false,
      recordHeaders: false,
      recordBody: false,
      // Strip query strings from captured URLs / network names (phone, tracking params).
      maskCapturedNetworkRequestFn: request => {
        if (request.name) {
          request.name = stripQueryString(request.name);
        }
        request.requestBody = undefined;
        request.responseBody = undefined;
        request.requestHeaders = undefined;
        request.responseHeaders = undefined;
        return request;
      },
    },
  };
}

/**
 * Every route is recorded, subject to the configured sample rate. To limit
 * replay to specific pages again, narrow this back down, e.g.
 * pathname === '/pricing' || pathname.startsWith('/checkout/').
 */
export function isReplayRoute(pathname: string): boolean {
  return true;
}

/** Coerce bootstrap/admin values to a 0–100 percentage. Defaults to 50. */
export function resolveReplaySampleRate(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 50;
  }
  const parsed =
    typeof value === 'number' ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(parsed)) {
    return 50;
  }
  return Math.min(100, Math.max(0, parsed));
}

/**
 * Sticky per-tab sample decision so pricing → checkout stays consistent.
 * At 100% always records; at 0% never records.
 */
export function shouldSampleReplaySession(
  sampleRatePercent: number,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof sessionStorage !==
  'undefined'
    ? sessionStorage
    : null,
): boolean {
  const rate = resolveReplaySampleRate(sampleRatePercent);
  if (rate >= 100) {
    return true;
  }
  if (rate <= 0) {
    return false;
  }

  if (!storage) {
    return Math.random() * 100 < rate;
  }

  const stored = storage.getItem(REPLAY_SAMPLE_STORAGE_KEY);
  if (stored === '1') {
    return true;
  }
  if (stored === '0') {
    return false;
  }

  const selected = Math.random() * 100 < rate;
  storage.setItem(REPLAY_SAMPLE_STORAGE_KEY, selected ? '1' : '0');
  return selected;
}
